import { useAppStateStore } from '@client/stores/AppStateStore';
import { useState } from 'react';

export interface ConnectionTestResult {
    success: boolean;
    latency?: number; // milliseconds
    timestamp: Date;
    error?: string;
}

/**
 * Hook for testing network connectivity
 * Provides functionality to ping the server and measure response time
 */
export function useConnectionTest() {
    const [testing, setTesting] = useState(false);
    const [lastResult, setLastResult] = useState<ConnectionTestResult | null>(null);
    const appStateStore = useAppStateStore();

    const testConnection = async (): Promise<ConnectionTestResult> => {
        setTesting(true);

        const startTime = performance.now();
        const timestamp = new Date();

        try {
            // Ping the server with a simple HEAD request to /api/health or similar
            // Using fetch with a short timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

            const response = await fetch(window.location.origin + '/api/health', {
                method: 'HEAD',
                signal: controller.signal,
                // Prevent caching to get accurate results
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache',
                },
            });

            clearTimeout(timeoutId);

            const endTime = performance.now();
            const latency = Math.round(endTime - startTime);

            const result: ConnectionTestResult = {
                success: response.ok,
                latency,
                timestamp,
            };

            setLastResult(result);

            // Update app state based on result
            if (response.ok) {
                appStateStore.actions.resetConnectionFailures();
            } else {
                appStateStore.actions.recordConnectionFailure();
            }

            setTesting(false);
            return result;
        } catch (error) {
            const endTime = performance.now();
            const latency = Math.round(endTime - startTime);

            const result: ConnectionTestResult = {
                success: false,
                latency: latency > 5000 ? undefined : latency, // Don't report latency if it timed out
                timestamp,
                error: error instanceof Error ? error.message : 'Connection test failed',
            };

            setLastResult(result);
            appStateStore.actions.recordConnectionFailure();

            setTesting(false);
            return result;
        }
    };

    return {
        testing,
        lastResult,
        testConnection,
    };
}
